import { Box, type Breakpoint } from "@mui/material";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import type React from "react";
import type { CSSProperties, FC, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { INK } from "../../../theme/tokens";
import { formatEmployeeStatusReason } from "../../../utils/employeeStatusReason";
import { getNurseDepartmentStatus, getNurseHrisEmployeeIdsAnyDepartment, isNurseOnLeaveOrSuspended } from "../../../utils/formatters";
import { type Nurse, NurseFullRoleShort } from "../../../utils/models";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import EmployeeStatusChip from "../Badges/EmployeeStatusChip";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "./SystemAutoComplete/SystemAutoComplete";

type Props = {
	nurses: SystemAutoCompleteOption<Nurse>[];
	value: SystemAutoCompleteOption<Nurse> | null;
	onChange: (event: React.SyntheticEvent, value: SystemAutoCompleteOption<Nurse> | null) => void;
	disabled?: boolean;
	width?: CSSProperties["width"] | Partial<Record<Breakpoint, CSSProperties["minWidth"]>>;
	fullWidth?: boolean;
	required?: boolean;
	label?: string;
	onOpenStateChange?: (event: SyntheticEvent, reason: AutocompleteCloseReason | undefined, isOpen: boolean) => void;
	autoFocus?: boolean;
	endAdornment?: (option: SystemAutoCompleteOption<Nurse>) => React.ReactNode;
	/**
	 * Render the options listbox inline instead of portaling it to `<body>`. Needed inside another
	 * popup (a popover/sheet), where a portaled listbox reads as a click *outside* the popup and
	 * closes it before the pick commits.
	 */
	disablePortal?: boolean;
	dataTestId?: string;
	/** A line under the field, e.g. why it's disabled; read out with the field by screen readers. */
	helperText?: string;
	/**
	 * Annie walkthrough anchor (`context/standards/frontend.md`). Explicit because this component doesn't
	 * spread rest props, and TypeScript exempts hyphenated JSX attributes from excess-property checking —
	 * so an unknown `data-*` prop is dropped silently rather than failing the build. Forwarded to
	 * `SystemAutoComplete`, which lands it on the field's root.
	 */
	"data-annie-target"?: string;
};

const filterStaffOptions = (options: SystemAutoCompleteOption<Nurse>[], state: { inputValue: string }) => {
	const input = state.inputValue.toLowerCase().trim();
	if (!input) return options;
	return options.filter((option) => {
		const nurse = option.value;
		return (
			option.label.toLowerCase().includes(input) ||
			String(nurse.id).includes(input) ||
			nurse.email?.toLowerCase().includes(input) ||
			nurse.phoneNumber?.includes(input) ||
			nurse.userId?.toLowerCase().includes(input) ||
			// APP-271: the HRIS employee ID (EE ID), which is the number Ops audits from. Shares the
			// roster column's source filter so the two agree on what an "Employee ID" is — an agency
			// clinician id is a different identifier and must not resolve a nurse here. Still NOT scoped
			// to a department: matching an id the person carries anywhere can never hide a hit.
			getNurseHrisEmployeeIdsAnyDepartment(nurse).some((id) => id.toLowerCase().includes(input))
		);
	});
};

export const StaffAutoComplete: FC<Props> = ({
	nurses,
	disabled,
	value,
	onChange,
	width = "250px",
	fullWidth,
	required = false,
	label,
	onOpenStateChange,
	autoFocus = false,
	endAdornment,
	disablePortal,
	dataTestId,
	helperText,
	"data-annie-target": annieTarget,
}) => {
	const roleColor = useRoleColors();
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	return (
		<SystemAutoComplete
			autoFocus={autoFocus}
			disablePortal={disablePortal}
			dataTestId={dataTestId}
			data-annie-target={annieTarget}
			fullWidth={fullWidth}
			onOpenStateChange={onOpenStateChange}
			options={nurses}
			value={value}
			onChange={onChange}
			justifyContent={"start"}
			filterOptions={filterStaffOptions}
			getOptionEndAdornment={(option) => {
				const consumerAdornment = endAdornment?.(option);
				const showStatus = isNurseOnLeaveOrSuspended(option.value, departmentId);
				if (!showStatus && !consumerAdornment) {
					return null;
				}
				const { status, reason, endDate } = getNurseDepartmentStatus(option.value, departmentId);
				return (
					<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
						{showStatus && (
							<EmployeeStatusChip
								status={status}
								reason={formatEmployeeStatusReason(reason, t)}
								endDate={endDate}
								dataTestId={`staff-autocomplete-status-${option.value.id}`}
							/>
						)}
						{consumerAdornment}
					</Box>
				);
			}}
			getOptionStartAdornment={(option) => {
				return (
					<InHouseTooltip title={NurseFullRoleShort[option.value.role]}>
						<Box
							sx={{
								width: "10px",
								height: "10px",
								borderRadius: "50%",
								backgroundColor: roleColor(option.value.role).dot,
							}}
						/>
					</InHouseTooltip>
				);
			}}
			textFieldProps={{
				required: required,
				placeholder: t("staff.autocomplete.placeholder"),
				label: label ?? t("staff.autocomplete.label"),
				disabled: disabled,
				defaultHelperText: helperText,
				helperTextColor: INK.MUTED,
				inputSx: {
					width: fullWidth ? "100%" : width,
				},
				reserveHeightForHelperText: false,
			}}
			disabled={disabled}
		/>
	);
};
